import { useMemo } from 'react'

import {
  EXECUTION_DETAIL_GROUPS,
  GroupedTagComparison,
  HEADLINER_GROUPS,
  TAG_GROUP_ORDER,
  TagComparisonSide,
  groupTagsByDimension,
  rankTagsForGame,
} from '../helpers/creativeTagPerformance'
import { useUATaxonomy } from './useUATaxonomy'

export interface CrownJewelsRows {
  /** Per-dimension comparison rows joining source + target stats. */
  byDimension: Map<string, GroupedTagComparison[]>
  /** `dimension → ranked tag list` for the source game (`rankTagsForGame(_, 'src')`). */
  sourceRanked: Map<string, GroupedTagComparison[]>
  /** `dimension → ranked tag list` for the target game (`rankTagsForGame(_, 'tgt')`). */
  targetRanked: Map<string, GroupedTagComparison[]>
  /** Headliner groups (intersected with whichever side has data). */
  headlinerGroups: string[]
  /** Execution-detail groups (gated behind the bottom toggle in the table). */
  executionGroups: string[]
  /** Unknown / new dimensions appended after the known order, alphabetised. */
  otherGroups: string[]
  /** Whether at least one bucket of groups has any rows worth rendering. */
  hasAnyContent: boolean
}

/**
 * Builds a per-dimension ranking for a single game by side (`'src'` / `'tgt'`).
 * Empty groups are kept in the map (with empty arrays) so callers can iterate
 * `TAG_GROUP_ORDER` without missing any explicit group; the per-row renderer
 * then decides whether to omit empty rows.
 */
function rankByDimension(byDimension: Map<string, GroupedTagComparison[]>, side: 'src' | 'tgt'): Map<string, GroupedTagComparison[]> {
  const out = new Map<string, GroupedTagComparison[]>()
  for (const [dimension, rows] of byDimension.entries()) {
    out.set(dimension, rankTagsForGame(rows, side))
  }
  return out
}

/**
 * Memoised data layer for {@link CrownJewelsTable}: joins source +
 * target tag stats per dimension, ranks each side's tags by mean Ad
 * Performance, then partitions the dimensions into headliner / execution /
 * other buckets in their canonical order. Returns the same shape regardless
 * of inputs so the table renderer can stay thin.
 */
export function useCrownJewelsRows(source: TagComparisonSide, target: TagComparisonSide, scopeMaxInstallShare: number): CrownJewelsRows {
  const { dimensionLabel, dimensionCode } = useUATaxonomy()
  const byDimension = useMemo(
    () => groupTagsByDimension(source, target, scopeMaxInstallShare, dimensionCode),
    [source, target, scopeMaxInstallShare, dimensionCode]
  )
  const sourceRanked = useMemo(() => rankByDimension(byDimension, 'src'), [byDimension])
  const targetRanked = useMemo(() => rankByDimension(byDimension, 'tgt'), [byDimension])

  // A group "has content" when at least one game has a non-empty ranked
  // list for it — surfacing groups where one side is empty is fine
  // (the empty cell just renders an em-dash) so the user still sees the
  // dimension is there, just not used by that game.
  const groupHasContent = (dimension: string): boolean => (sourceRanked.get(dimension)?.length ?? 0) > 0 || (targetRanked.get(dimension)?.length ?? 0) > 0

  const headlinerGroups = useMemo(() => HEADLINER_GROUPS.filter(groupHasContent), [sourceRanked, targetRanked]) // eslint-disable-line react-hooks/exhaustive-deps
  const executionGroups = useMemo(() => EXECUTION_DETAIL_GROUPS.filter(groupHasContent), [sourceRanked, targetRanked]) // eslint-disable-line react-hooks/exhaustive-deps
  // Same fallback strategy as `CreativeTagsByGroupSection`: any dimension
  // present in the data we don't recognise still renders, alphabetised at
  // the bottom, so newly-introduced groups never disappear.
  const otherGroups = useMemo(() => {
    const known = new Set(TAG_GROUP_ORDER)
    const others: string[] = []
    for (const dim of [...sourceRanked.keys(), ...targetRanked.keys()]) {
      if (!known.has(dim) && groupHasContent(dim) && !others.includes(dim)) others.push(dim)
    }
    return others.sort((a, b) => dimensionLabel(a).localeCompare(dimensionLabel(b)))
  }, [sourceRanked, targetRanked, dimensionLabel]) // eslint-disable-line react-hooks/exhaustive-deps

  const hasAnyContent = headlinerGroups.length > 0 || executionGroups.length > 0 || otherGroups.length > 0

  return { byDimension, sourceRanked, targetRanked, headlinerGroups, executionGroups, otherGroups, hasAnyContent }
}
